import { Component, Prop } from 'vue-property-decorator'
import Vue, { CreateElement } from '@src/vue'

import { Caption, H6, Overline, Subtitle2 } from '@src/elements/typography/typography'
import { Icon } from '@src/elements/icon/icon'
import { Tooltip } from '@src/elements/tooltip/tooltip'
import { ExerciseInfoHistory } from '@src/routes/members/components/results-history/exercise-history-popup/exercise-info-history'
import { ExerciseResponse } from '@src/data/exercise-response'
import { ExercisePreview } from '../exercise-preview/exercise-preview'
import { ExerciseResult } from '@src/data/workout-results'
import WorkoutData from '@src/data/workout-data'

import './exercise-card-history.sass'

@Component({
  computed: {
    availableExercises () {
      return this.$store.state.availableExercises
    }
  }
})
export class ExerciseCardHistory extends Vue {

  @Prop({ required: false, default: 'custom' })
  type: 'home' | 'custom'

  @Prop({ required: true })
  exerciseResult: ExerciseResult

  @Prop({ required: true })
  renderColorByValue: (val: number) => string

  @Prop({ required: false, default: null })
  workout: WorkoutData | null

  private availableExercises: ExerciseResponse[]
  private addTooltip = false
  private showExerciseInfo: boolean = false

  constructor () {
    super()
  }

  updated () {
    this.checkTitle()
  }

  mounted () {
    this.checkTitle()
  }

  // check if title doesn't fit
  checkTitle () {
    let title = this.$refs.title as Vue
    this.addTooltip = title.$el.clientWidth < title.$el.scrollWidth
  }

  render (h: CreateElement) {
    const exercise = this.getExercise(this.exerciseResult.exerciseId)
    if (!exercise) {
      // Gracefully handle missing exercise by showing a placeholder
      return <div class='exercise-results-row'>
        <div class='left'>
          <div class='exercise-preview'>
            <img class='exercise-preview' src='/assets/icon/brand/default-exercise.jpg' />
          </div>
          <div class='exercise-details'>
            <Overline class='header'>{this.$t('PAGE.CONTENT.WORKOUTS.NON_TRACKED')}</Overline>
            <H6 class='title' ref='title'>{this.$t('PAGE.CONTENT.EXERCISES.UNAVAILABLE_EXERCISE')}</H6>
          </div>
        </div>
        {this.type !== 'home' && this.renderCustomExerciseRight(h)}
      </div>
    }
    return <div class='exercise-results-row'>
      {this.type === 'home' ? this.renderHomeExercise(h, exercise) : this.renderCustomExerciseLeft(h, exercise)}
      {this.type !== 'home' && this.renderCustomExerciseRight(h)}
      <div class='history-flex-container info-wrapper'>
        <div onClick={this.showHideInfo}>
          <Caption class='details-link'>{this.$t('VIEW_DETAILS')}</Caption>
        </div>
      </div>
      <div class='mobile-results-wrapper'>
        {this.renderCustomExerciseRight(h)}
      </div>

      {this.showExerciseInfo ?
        <ExerciseInfoHistory exercise={exercise}
                             result={this.exerciseResult}
                             showModal={this.showExerciseInfo}
                             closeCallback={this.showHideInfo} /> : null}
      </div>
  }

  renderExerciseTypeLabelOnPortrait (h: CreateElement, exercise) {
    return (
      <div class='portrait-exercise-history-type'>
      {exercise.state === 'beta' ? <Overline class='label'>{this.$t('PAGE.CONTENT.EXERCISES.BETA')}</Overline> : null}
      {exercise.custom ? <Overline class='label'>{this.$t('PAGE.CONTENT.EXERCISES.CUSTOM')}</Overline> : null}
    </div>
    )
  }

  renderHomeExercise (h: CreateElement, exercise) {
    return <div class='left'>
        {this.renderPreview(h, exercise)}
        <div class='exercise-details'>
          {this.renderHeader(h, exercise)}
          {this.renderExerciseTitle(h, exercise)}
          {this.renderExerciseTypeLabelOnPortrait(h, exercise)}
          <div class='specs'>
            {exercise.state === 'beta' ? <Overline class='label'>{this.$t('PAGE.CONTENT.EXERCISES.BETA')}</Overline> : null}
            {exercise.custom ? <Overline class='label'>{this.$t('PAGE.CONTENT.EXERCISES.CUSTOM')}</Overline> : null}
          </div>
        </div>
      </div>
  }

  renderCustomExerciseLeft (h: CreateElement, exercise) {
    return <div class='left'>
        {this.renderPreview(h, exercise)}
        <div class='exercise-details'>
          {this.renderHeader(h, exercise)}
          {this.renderExerciseTitle(h, exercise)}
          {this.renderExerciseTypeLabelOnPortrait(h, exercise)}
          <div class='specs'>
            {exercise.state === 'beta' ? <Overline class='label'>{this.$t('PAGE.CONTENT.EXERCISES.BETA')}</Overline> : null}
            {exercise.custom ? <Overline class='label'>{this.$t('PAGE.CONTENT.EXERCISES.CUSTOM')}</Overline> : null}
            <div onClick={this.showHideInfo} class='info-wrapper'>
              <Caption class='details-link'>{this.$t('VIEW_DETAILS')}
                <Icon name='/util/info' class='info-btn' /></Caption>
            </div>
          </div>
        </div>
      </div>
  }

  renderCustomExerciseRight (h: CreateElement) {
    return <div class='right'>
        <div class='result'>
          <Icon name='brand/heartbeat' class={`${this.renderColorByValue((this.exerciseResult.repetitionCount || 0) / (this.exerciseResult.repetitionGoal || 1))}`} />
          <Subtitle2>{`${this.$t('REPETITIONS')}`}</Subtitle2>
          <span class={`${this.renderColorByValue((this.exerciseResult.repetitionCount || 0) / (this.exerciseResult.repetitionGoal || 1))}`}>
          <Subtitle2>{`${this.exerciseResult.repetitionCount || 0}/${this.exerciseResult.repetitionGoal || '-'}`}</Subtitle2>
        </span>
        </div>
        <div class='result'>
          <Icon name={'workout/score'} class={`${this.renderColorByValue((this.exerciseResult.pixformanceScore || 0) / 100)}`} />
          <Subtitle2>{`${this.$t('PAGE.MEMBERS.STATUS.PIX_SCORE')}`}</Subtitle2>
          <span class={`${this.renderColorByValue((this.exerciseResult.pixformanceScore || 0) / 100)}`}>
          <Subtitle2>{`${this.exerciseResult.pixformanceScore || 0}/100`}</Subtitle2>
        </span>
        </div>
        <div class='result'>
          <Icon name={'brand/tempo'} class={`${this.renderColorByValue((this.exerciseResult.tempoScore || 0) / 100)}`} />
          <Subtitle2>{`${this.$t('PAGE.MEMBERS.TRAINING.HISTORY.TEMPO_SCORE')}`}</Subtitle2>
          <span class={`${this.renderColorByValue((this.exerciseResult.tempoScore || 0) / 100)}`}>
          <Subtitle2>{`${this.exerciseResult.tempoScore || 0}/100`}</Subtitle2>
        </span>
        </div>
        <div class='result'>
          <Icon name={'brand/accuracy'} class={`${this.renderColorByValue((this.exerciseResult.precisionScore || 0) / 100)}`} />
          <Subtitle2>{`${this.$t('PAGE.MEMBERS.TRAINING.HISTORY.ACCURACY')}`}</Subtitle2>
          <span class={`${this.renderColorByValue((this.exerciseResult.precisionScore || 0) / 100)}`}>
          <Subtitle2>{` ${this.exerciseResult.precisionScore || 0}/100`}</Subtitle2>
        </span>
        </div>
        <div class='result'>
          <Icon name={'brand/range-of-motion'} class={`${this.renderColorByValue((this.exerciseResult.rangeScore || 0) / 100)}`} />
          <Subtitle2>{`${this.$t('PAGE.MEMBERS.TRAINING.HISTORY.RANGE_OF_MOTION')} `}</Subtitle2>
          <span class={`${this.renderColorByValue((this.exerciseResult.rangeScore || 0) / 100)}`}>
          <Subtitle2>{` ${this.exerciseResult.rangeScore || 0}/100`}</Subtitle2>
        </span>
        </div>
      </div>
  }

  renderPreview (h: CreateElement, exercise) {
    return <div class='exercise-preview'>
      <ExercisePreview exercise={exercise} />
    </div>
  }

  renderHeader (h: CreateElement, exercise) {
    return <Overline class='header'>
      {exercise.is2DTracked() && exercise.is3DTracked() ? this.$t('PAGE.CONTENT.WORKOUTS.TRACKED')
        : exercise.is2DTracked() ? this.$t('PAGE.CONTENT.EXERCISES.2D_TRACKED')
          : exercise.is3DTracked() ? this.$t('PAGE.CONTENT.EXERCISES.3D_TRACKED')
            : this.$t('PAGE.CONTENT.WORKOUTS.NON_TRACKED')}
    </Overline>
  }

  renderExerciseTitle (h: CreateElement, exercise) {
    return this.addTooltip ?
      <Tooltip content={exercise.getName()}>
        <H6 class='title' ref='title'>{exercise.getName()}</H6>
      </Tooltip>
      : <H6 class='title' ref='title'>{exercise.getName()}</H6>
  }

  showHideInfo () {
    this.showExerciseInfo = !this.showExerciseInfo
  }

  getExercise (id: number): ExerciseResponse | undefined {
    // First, try to find the exercise in the workout's embedded exercise data
    // This ensures we get the exercise data even if it's no longer in availableExercises
    // (e.g., due to custom status changes or exercise group restrictions)
    if (this.workout && this.workout.workoutExercises) {
      const workoutExercise = this.workout.workoutExercises.find(we => we.exercise && we.exercise.id === id)
      if (workoutExercise && workoutExercise.exercise) {
        return workoutExercise.exercise
      }
    }

    // Fall back to availableExercises if not found in workout
    return this.availableExercises.find(exercise => exercise.id === id)
  }
}
